import React from 'react';
import { FeeForm } from './FeeForm';
import { DocumentsContainer } from '../DocumentsContainer';
import { IFeeEditLayout } from './IFeeEditLayout';
import { FeeEditLayout, FeeEditLayoutGroup, FeeEditStickyBlock } from './FeeEditLayout';
import { Disbursements } from '../disbursements/Disbursements';
import { ISettlementItemForm, createDefaultSettlementFeeForm } from 'types/order/fees/ICreateSettlementItemForm';
import { FieldArray, useField } from 'formik';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { Button } from 'ui-toolkit';
import { PayeeIsEditable } from '../../PayeeIsEditable';
import { ISettlementAndFeeCategoryDefinitionData } from 'types/order/fees/ISettlementAndFeeCategoryDefinition';
import { availablePropertiesBySelection } from './useAvailablePropertiesBySelection';
import { useGetDisbursementsForSettlementItem } from '../useGetDisbursementsForSettlementItem';
import { eFeePropertyNames } from 'types/order/fees/eFeePropertyNames';
import { eTransactionCategory } from '../../../../../ui-toolkit/enums/eTransactionType';

export interface IFeeListProps extends IFeeEditLayout {
  lineItem: ISettlementItemForm;
  options: ISettlementAndFeeCategoryDefinitionData[];
  hasErrors: boolean;
  isDirty: boolean;
}
const borderAndBackground = (isDirty: boolean, isReadOnly: boolean, hasErrors: boolean) => {
  switch (true) {
    case hasErrors:
      return 'border-red-100 bg-red-50';
    case isDirty:
      return 'border-yellow-100 bg-yellow-50';
    case isReadOnly:
      return 'border-coolGray-300 bg-coolGray-50';
    default:
      return 'border-coolGray-100 bg-white';
  }
};
export const FeeList: React.FC<IFeeListProps> = ({ layout, lineItem, options, isDirty, hasErrors }) => {
  const [, , { setTouched }] = useField(`statementFees`);
  const currentOrder = useSelectCurrentOrder();
  const isRefinance = currentOrder.data.transaction.transaction_category === eTransactionCategory.Refinance;
  const payeeIsEditable = PayeeIsEditable(lineItem);
  const isReadOnly = !lineItem.isNew && !options?.[0]?.editable;
  const properties = availablePropertiesBySelection(options, lineItem.statementSettlementAndFeeCategoryDefinitionId);
  const disbursements = useGetDisbursementsForSettlementItem(lineItem.statementFees.map((t) => t.disbursementIds));
  const [{ value: name }] = useField<string>(`stringSettlementStatementPropertyValues.${eFeePropertyNames.Name}`);
  return (
    <FieldArray name={`statementFees`}>
      {({ push, remove }) => (
        <FeeEditLayout layout={layout}>
          {lineItem.statementFees.map((item, index) => (
            <FeeEditLayoutGroup
              layout={layout}
              key={item.key}
              className={`z-[${lineItem.statementFees.length - index}]`}
            >
              <FeeEditStickyBlock layout={layout} className={borderAndBackground(isDirty, isReadOnly, hasErrors)}>
                <FeeForm
                  baseName={`statementFees.[${index}]`}
                  fee={item}
                  remove={
                    lineItem.statementFees.length > 1
                      ? () => {
                          remove(index);
                          setTouched(true);
                        }
                      : undefined
                  }
                  isReadOnly={isReadOnly}
                  options={options}
                  payeeIsEditable={payeeIsEditable}
                  feeIndex={lineItem.statementFees.length > 1 ? index + 1 : undefined}
                  feeName={name}
                  isRefinance={isRefinance}
                />
                {layout === 'Two Column' && (
                  <Disbursements list={disbursements[index]} fee={item} order={currentOrder.data} feeName={name} />
                )}
              </FeeEditStickyBlock>
              <FeeEditStickyBlock layout={layout} className={`-z-[1] ${borderAndBackground(isDirty, false, false)}`}>
                <DocumentsContainer baseName={`statementFees.[${index}]`} />
              </FeeEditStickyBlock>
            </FeeEditLayoutGroup>
          ))}
          {!isReadOnly && properties.canAddMoreFees && (
            <div className={'m-4 ml-0'}>
              <Button
                disabled={!lineItem.statementSettlementAndFeeCategoryDefinitionId}
                onClick={() => {
                  push(
                    createDefaultSettlementFeeForm({
                      statementSettlementAndFeeCategoryDefinitionId:
                        lineItem.statementSettlementAndFeeCategoryDefinitionId,
                    })
                  );
                  setTouched(true);
                }}
                intent={`ghost`}
              >
                + Add Fee
              </Button>
            </div>
          )}
        </FeeEditLayout>
      )}
    </FieldArray>
  );
};
